import { useCallback, useEffect, useRef, useState } from "react";

export const OAUTH_NAVIGATION_TIMEOUT_MS = 15000;

export function useOAuthPending({
  errorMessage = "Couldn't connect. Please try again.",
  onPendingChange,
}: {
  errorMessage?: string;
  onPendingChange?: (pending: boolean) => void;
} = {}) {
  // Which provider's round-trip is in flight (drives the per-button spinner and
  // disables the others). Null when idle.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Monotonic id for the active attempt; late continuations compare against it
  // and no-op, so a rejection from a superseded attempt can't clobber a newer
  // one.
  const attemptIdRef = useRef(0);
  const pendingRef = useRef(false);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // signIn() isn't abortable, so an in-flight attempt's promise can settle after
  // this component unmounts (e.g. the modal closed). The attempt-id guard only
  // protects against same-instance superseding — a remounted instance has its
  // own counter — so we also gate on mounted state to keep a stale rejection
  // from calling back into a still-mounted host (clobbering a newer attempt).
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Re-assert on (re)mount, not just clear on cleanup: under React Strict Mode
    // the mount→unmount→remount simulation runs the cleanup (setting false)
    // before the second mount, so without this the flag stays false for the rest
    // of the component's life and failOAuth() early-returns forever.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearStuckTimer = useCallback(() => {
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
  }, []);

  const failOAuth = useCallback(
    (attemptId: number) => {
      if (!isMountedRef.current) return; // unmounted — stale, host already reset
      if (attemptIdRef.current !== attemptId) return; // superseded / abandoned
      clearStuckTimer();
      pendingRef.current = false;
      setPendingId(null);
      // Notified synchronously (not via a post-render effect) so a host's
      // sibling-disable state updates in the same commit, with no lag window.
      onPendingChange?.(false);
      setError(errorMessage);
    },
    [clearStuckTimer, errorMessage, onPendingChange]
  );

  const handleSignIn = useCallback(
    async (option: { id: string; signIn: () => Promise<unknown> | void }) => {
      if (pendingRef.current) return;
      pendingRef.current = true;
      const attemptId = ++attemptIdRef.current;
      clearStuckTimer();
      setPendingId(option.id);
      onPendingChange?.(true);
      setError(null);
      // Arm the recovery timer up front so a request that never settles can't
      // leave the spinner stuck — a real redirect unmounts the host first.
      stuckTimerRef.current = setTimeout(
        () => failOAuth(attemptId),
        OAUTH_NAVIGATION_TIMEOUT_MS
      );
      try {
        await option.signIn();
        // Resolved: on success the browser is navigating away; the timer is the
        // safety net if navigation is blocked.
      } catch {
        failOAuth(attemptId);
      }
    },
    [clearStuckTimer, failOAuth, onPendingChange]
  );

  // Clear the fallback timer if the host unmounts mid-attempt.
  useEffect(() => clearStuckTimer, [clearStuckTimer]);

  // On unmount, declare "not pending" so a host's sibling-disable state doesn't
  // stay stuck if we unmount mid-attempt (e.g. the modal closes during an
  // in-flight sign-in, which drops this component without resolving it).
  useEffect(() => () => onPendingChange?.(false), [onPendingChange]);

  return { pendingId, error, handleSignIn };
}
